/**
 * Pure selectors for deriving request-ready state from SourceControlToggleState.
 *
 * These exist so that useEvidenceSourceConfig (React memo path) and
 * useChatStream (fresh Jotai store.get() path) share identical logic.
 * All functions are pure — no hooks, no React, no side effects.
 */
import type { SourceControlToggleState } from './sourceControlAtoms';
import { UNCOLLECTED_COLLECTION_ID } from './sourceControlAtoms';
/**
 * Whether any collection-based search is active.
 *
 * Legacy mode: only `myLibrary` counts.
 * Unified mode: either toggle means a collection is selected.
 */
export function getHasActiveLibrary(
  state: SourceControlToggleState,
  isUnified: boolean
): boolean {
  return isUnified ? state.myLibrary || state.webCollections : state.myLibrary;
}

/**
 * Collection IDs to send as `user_doc_collection_ids`.
 *
 * Legacy mode: only `selectedCollectionIds` (+ uncollected sentinel).
 * Unified mode: merges both buckets — all IDs are collection UUIDs.
 */
export function getActiveCollectionIds(
  state: SourceControlToggleState,
  isUnified: boolean
): string[] {
  if (isUnified) {
    if (!state.myLibrary && !state.webCollections) return [];
    const ids: string[] = [
      ...state.selectedCollectionIds,
      ...state.selectedWebCollectionIds,
    ];
    if (state.includeUncollected) {
      ids.push(UNCOLLECTED_COLLECTION_ID);
    }
    return ids;
  }

  // Legacy path
  if (!state.myLibrary) return [];
  const ids: string[] = [...state.selectedCollectionIds];
  if (state.includeUncollected) {
    ids.push(UNCOLLECTED_COLLECTION_ID);
  }
  return ids;
}

/** 24-character hex string = legacy MongoDB ObjectId (source list ID). */
const LEGACY_HEX_RE = /^[0-9a-f]{24}$/i;

/**
 * Partition active IDs into UUID-format (→ collection_ids) and legacy hex (→ source_list_ids).
 *
 * During the unified migration, `allCollections` includes unmigrated source lists
 * whose IDs are 24-char hex. The backend's `collection_ids` field only accepts UUIDs,
 * so legacy IDs must be routed through the existing `source_list_ids` field instead.
 */
export function partitionCollectionIds(activeIds: string[]): {
  collectionIds: string[];
  legacySourceListIds: string[];
} {
  const collectionIds: string[] = [];
  const legacySourceListIds: string[] = [];
  for (const id of activeIds) {
    if (LEGACY_HEX_RE.test(id)) {
      legacySourceListIds.push(id);
    } else {
      collectionIds.push(id);
    }
  }
  return { collectionIds, legacySourceListIds };
}

export interface WorkspaceTaggedCollection {
  collection_id: string;
  workspace_id?: string | null;
  legacy_source_list_id?: string | null;
}

export interface WorkspaceCollectionScopeOptions {
  activeWorkspaceId: string | null;
  collections: readonly WorkspaceTaggedCollection[] | undefined;
  /** The collection list could not be read, so no selected ID can be verified. */
  isCollectionsUnavailable: boolean;
  isUnified: boolean;
}

/**
 * Request-only view of the selection without other workspaces' collections.
 * Never write the result back: the persisted selection is user-global.
 */
export function scopeSourceControlToWorkspace(
  state: SourceControlToggleState,
  {
    activeWorkspaceId,
    collections,
    isCollectionsUnavailable,
    isUnified,
  }: WorkspaceCollectionScopeOptions
): SourceControlToggleState {
  if (!activeWorkspaceId) return state;

  let isExcluded: (id: string) => boolean;
  if (isCollectionsUnavailable) {
    isExcluded = () => true;
  } else {
    const foreignIds = new Set<string>();
    for (const collection of collections ?? []) {
      if (!collection.workspace_id || collection.workspace_id === activeWorkspaceId) {
        continue;
      }
      foreignIds.add(collection.collection_id);
      if (collection.legacy_source_list_id) {
        foreignIds.add(collection.legacy_source_list_id);
      }
    }
    if (foreignIds.size === 0) return state;
    isExcluded = (id) => foreignIds.has(id);
  }

  const selectedCollectionIds = state.selectedCollectionIds.filter(
    (id) => !isExcluded(id)
  );
  const selectedWebCollectionIds = isUnified
    ? state.selectedWebCollectionIds.filter((id) => !isExcluded(id))
    : state.selectedWebCollectionIds;

  const libraryChanged =
    selectedCollectionIds.length !== state.selectedCollectionIds.length;
  const webChanged =
    selectedWebCollectionIds.length !== state.selectedWebCollectionIds.length;
  if (!libraryChanged && !webChanged) return state;

  const shouldResetLibrary =
    libraryChanged &&
    state.libraryMode === 'collections' &&
    selectedCollectionIds.length === 0;
  const shouldResetWeb =
    webChanged &&
    state.webCollectionsMode === 'collections' &&
    selectedWebCollectionIds.length === 0;

  return {
    ...state,
    selectedCollectionIds,
    selectedWebCollectionIds,
    ...(shouldResetLibrary
      ? { myLibrary: false, libraryMode: 'all' as const, includeUncollected: false }
      : {}),
    ...(shouldResetWeb
      ? { webCollections: false, webCollectionsMode: 'all' as const }
      : {}),
  };
}

/**
 * Whether to set `include_user_docs: true` in the request payload.
 */
export function getIncludeUserDocs(
  state: SourceControlToggleState,
  isUnified: boolean,
  isFeatureEnabled: boolean,
  hasUserDocs: boolean
): boolean {
  return isFeatureEnabled && hasUserDocs && getHasActiveLibrary(state, isUnified);
}

export interface PersistedSourceControlNormalizationOptions {
  validLibraryCollectionIds?: readonly string[];
  validWebCollectionIds?: readonly string[];
  /**
   * Prune list for library collections (EVI-1368 / EVI-2407).
   * Call sites pass `libraryCollectionAllowlistIds` (enforced ∪ optional),
   * not the enforced-only list — optional IDs are valid member selections.
   * When provided, persisted `selectedCollectionIds` are pruned against the
   * intersection of `validLibraryCollectionIds` and this list. `null` or
   * omitted = no enforcement (per useTeamEvidenceEnforcement's "empty = allow
   * all" contract, see EVI-1367).
   */
  enforcedLibraryCollectionIds?: readonly string[] | null;
  /** EVI-1368: Team enforcement allowlist for web collections. */
  enforcedWebCollectionIds?: readonly string[] | null;
}

/**
 * EVI-2407: the prune list is `enforced ∪ optional` (`libraryCollectionAllowlistIds`).
 * Call sites pass that allowlist first; `enforced` is only the fallback when the
 * allowlist field is absent (undefined), so incomplete mocks still prune.
 * `null` means no library enforcement and must not fall through to a non-null
 * enforced list.
 */
export function resolveLibraryCollectionPruneIds(
  allowlist: readonly string[] | null | undefined,
  enforced: readonly string[] | null | undefined
): readonly string[] | null | undefined {
  return allowlist === undefined ? enforced : allowlist;
}

/** Intersect a "valid" set with an optional "enforced" allowlist. */
function buildAllowedIdSet(
  valid: readonly string[] | undefined,
  enforced: readonly string[] | null | undefined
): Set<string> | null {
  const validSet = valid ? new Set(valid) : null;
  const enforcedSet = enforced && enforced.length > 0 ? new Set(enforced) : null;
  if (!validSet && !enforcedSet) return null;
  if (!enforcedSet) return validSet;
  if (!validSet) return enforcedSet;
  return new Set([...validSet].filter((id) => enforcedSet.has(id)));
}

/**
 * Drops persisted collection/source-list IDs that no longer exist remotely OR
 * fall outside the team enforcement allowlist (EVI-1368).
 *
 * When a bucket is in explicit "collections" mode and every persisted ID is stale,
 * reset that bucket instead of silently widening the next request to "all".
 */
export function normalizePersistedSourceControlState(
  state: SourceControlToggleState,
  {
    validLibraryCollectionIds,
    validWebCollectionIds,
    enforcedLibraryCollectionIds,
    enforcedWebCollectionIds,
  }: PersistedSourceControlNormalizationOptions
): SourceControlToggleState {
  const libraryIdSet = buildAllowedIdSet(
    validLibraryCollectionIds,
    enforcedLibraryCollectionIds
  );
  const webIdSet = buildAllowedIdSet(validWebCollectionIds, enforcedWebCollectionIds);

  // Persisted state from older app versions may be missing array fields entirely.
  // Treat any missing/non-array bucket as empty so we never throw on .filter().
  const persistedSelectedCollectionIds = Array.isArray(state.selectedCollectionIds)
    ? state.selectedCollectionIds
    : [];
  const persistedSelectedWebCollectionIds = Array.isArray(state.selectedWebCollectionIds)
    ? state.selectedWebCollectionIds
    : [];

  const selectedCollectionIds = libraryIdSet
    ? persistedSelectedCollectionIds.filter((id) => libraryIdSet.has(id))
    : persistedSelectedCollectionIds;
  const selectedWebCollectionIds = webIdSet
    ? persistedSelectedWebCollectionIds.filter((id) => webIdSet.has(id))
    : persistedSelectedWebCollectionIds;

  // We "changed" if any stale ID was filtered out OR if persisted state was
  // missing the array entirely (in which case we want to backfill it as []).
  const collectionsChanged =
    selectedCollectionIds.length !== persistedSelectedCollectionIds.length ||
    persistedSelectedCollectionIds !== state.selectedCollectionIds;
  const webCollectionsChanged =
    selectedWebCollectionIds.length !== persistedSelectedWebCollectionIds.length ||
    persistedSelectedWebCollectionIds !== state.selectedWebCollectionIds;

  const shouldResetLibrarySelection =
    libraryIdSet !== null &&
    state.libraryMode === 'collections' &&
    persistedSelectedCollectionIds.length > 0 &&
    selectedCollectionIds.length === 0;
  const shouldResetWebSelection =
    webIdSet !== null &&
    state.webCollectionsMode === 'collections' &&
    persistedSelectedWebCollectionIds.length > 0 &&
    selectedWebCollectionIds.length === 0;

  if (
    !collectionsChanged &&
    !webCollectionsChanged &&
    !shouldResetLibrarySelection &&
    !shouldResetWebSelection
  ) {
    return state;
  }

  return {
    ...state,
    selectedCollectionIds,
    selectedWebCollectionIds,
    ...(shouldResetLibrarySelection
      ? {
          myLibrary: false,
          libraryMode: 'all' as const,
          includeUncollected: false,
        }
      : {}),
    ...(shouldResetWebSelection
      ? {
          webCollections: false,
          webCollectionsMode: 'all' as const,
        }
      : {}),
  };
}
